import { useState } from "react";
import { Check, ArrowRight, Sparkles, Users, Crown } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import { toast } from "sonner";
import SkoolInfoModal from "@/components/SkoolInfoModal";

const Programs = ({ standalone, onOpenBooking }) => {
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSkoolModalOpen, setIsSkoolModalOpen] = useState(false);

  const programs = [
    {
      id: "decoded_monthly",
      badge: t("programs.decoded_badge"),
      icon: Users,
      name: t("programs.decoded_name"),
      tagline: t("programs.decoded_tagline"),
      price: 47,
      priceLabel: t("programs.priceMonth"),
      description: t("programs.decoded_desc"),
      features: [
        t("programs.decoded_f1"),
        t("programs.decoded_f2"),
        t("programs.decoded_f3"),
        t("programs.decoded_f4"),
        t("programs.decoded_f5"),
        t("programs.decoded_f6"),
        t("programs.decoded_f7"),
      ],
      cta: t("programs.decoded_cta"),
      accent: "#4A5543"
    },
    {
      id: "transformation_full",
      badge: t("programs.transformation_badge"),
      icon: Sparkles,
      name: t("programs.transformation_name"),
      tagline: t("programs.transformation_tagline"),
      price: 3300,
      priceLabel: t("programs.priceOr"),
      description: t("programs.transformation_desc"),
      features: [
        t("programs.transformation_f1"),
        t("programs.transformation_f2"),
        t("programs.transformation_f3"),
        t("programs.transformation_f4"),
        t("programs.transformation_f5"),
        t("programs.transformation_f6"),
        t("programs.transformation_f7"),
        t("programs.transformation_f8"),
      ],
      cta: t("programs.transformation_cta"),
      popular: true,
      accent: "#C5A059"
    },
    {
      id: "elite_1on1",
      badge: t("programs.elite_badge"),
      icon: Crown,
      name: t("programs.elite_name"),
      tagline: t("programs.elite_tagline"),
      price: 8500,
      priceLabel: t("programs.priceMonths"),
      description: t("programs.elite_desc"),
      features: [
        t("programs.elite_f1"),
        t("programs.elite_f2"),
        t("programs.elite_f3"),
        t("programs.elite_f4"),
        t("programs.elite_f5"),
        t("programs.elite_f6"),
        t("programs.elite_f7"),
        t("programs.elite_f8"),
      ],
      cta: t("programs.elite_cta"),
      limited: true,
      accent: "#2C3524"
    }
  ];

  const handlePurchase = async (packageId) => {
    // Show info modal for the Skool community instead of jumping straight to checkout
    if (packageId === "decoded_monthly") {
      setIsSkoolModalOpen(true);
      return;
    }
    if (packageId === "elite_1on1" || packageId === "transformation_full") {
      onOpenBooking();
      return;
    }

    setIsLoading(true);
    setSelectedPackage(packageId);

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Unable to start checkout. Please try again.");
    } finally {
      setIsLoading(false);
      setSelectedPackage(null);
    }
  };

  return (
    <section
      id="programs"
      data-testid="programs-section"
      className={`section-padding ${standalone ? 'pt-32' : ''}`}
      style={{ background: '#F9F8F6' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <span className="overline mb-4 block">{t("programs.overline")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl text-[#2C3524] leading-[1.1] mb-8">
            {t("programs.titlePart1")}<br />
            <span className="text-[#C5A059] italic">{t("programs.titlePart2")}</span>
          </h2>
          <p className="text-[#4A5543] text-lg md:text-xl font-light leading-relaxed">
            {t("programs.intro")}
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {programs.map((program) => (
            <div
              key={program.id}
              data-testid={`program-card-${program.id}`}
              className={`relative flex flex-col p-8 md:p-10 rounded-3xl transition-all duration-500 ${
                program.popular 
                  ? 'bg-white border-2 border-[#C5A059] shadow-[0_20px_60px_rgba(197,160,89,0.15)] md:-translate-y-4' 
                  : 'bg-white border border-[#E3DAC9] hover:border-[#C5A059] hover:-translate-y-2'
              }`}
            >
              {program.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C5A059] text-white px-5 py-1.5 text-xs font-medium tracking-[0.15em] uppercase rounded-full whitespace-nowrap">
                  {t("programs.popular")}
                </div>
              )}

              {/* Badge & Icon */}
              <div className="flex items-center justify-between mb-6">
                <span 
                  className="text-xs tracking-[0.2em] uppercase font-medium"
                  style={{ color: program.accent }}
                >
                  {program.badge}
                </span>
                <program.icon 
                  className="w-6 h-6" 
                  style={{ color: program.accent }}
                  strokeWidth={1.5} 
                />
              </div>

              {/* Name & Tagline */}
              <div className="mb-6">
                <h3 className="font-heading text-2xl md:text-3xl text-[#2C3524] mb-1 leading-tight">
                  {program.name}
                </h3>
                <p className="text-[#4A5543]/70 text-sm italic">
                  {program.tagline}
                </p>
              </div>

              {/* Price */}
              <div className="mb-6 pb-6 border-b border-[#E3DAC9]">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl md:text-5xl font-heading text-[#2C3524]">
                    ${program.price.toLocaleString()}
                  </span>
                  <span className="text-[#4A5543]/70 text-sm">
                    {program.priceLabel}
                  </span>
                </div>
              </div>

              {/* Description */}
              <p className="text-[#4A5543] font-light mb-6 leading-relaxed">
                {program.description}
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-grow">
                {program.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check 
                      className="w-4 h-4 flex-shrink-0 mt-1" 
                      style={{ color: program.accent }}
                      strokeWidth={2.5}
                    />
                    <span className={`text-sm leading-relaxed ${
                      feature.includes('plus:') 
                        ? 'text-[#2C3524] font-medium italic' 
                        : 'text-[#4A5543]'
                    }`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handlePurchase(program.id)}
                disabled={isLoading && selectedPackage === program.id}
                className={`w-full py-4 rounded-full font-medium transition-all duration-300 group ${
                  program.popular
                    ? 'bg-[#C5A059] text-white hover:bg-[#DCC286] hover:shadow-lg'
                    : 'bg-[#2C3524] text-white hover:bg-[#3F4B34]'
                }`}
                data-testid={`program-checkout-${program.id}`}
              >
                {isLoading && selectedPackage === program.id ? (
                  t("programs.processing")
                ) : (
                  <>
                    {program.cta}
                    <ArrowRight className="inline-block ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>

              {program.limited && (
                <p className="mt-4 text-center text-xs text-[#4A5543]/70 tracking-wider uppercase">
                  {t("programs.limitedNote")}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Guarantee */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white border border-[#E3DAC9]">
            <Sparkles className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
            <span className="text-[#2C3524] font-light text-sm md:text-base">
              {t("programs.guarantee")}
            </span>
          </div>
        </div>
      </div>

      {/* Skool Community Info Modal */}
      <SkoolInfoModal
        isOpen={isSkoolModalOpen}
        onClose={() => setIsSkoolModalOpen(false)}
      />
    </section>
  );
};

export default Programs;
